import { useSettings } from '../../shared/hooks/useSettings';
import { GeneralAcctInfoPanel } from '../components/GeneralAcctInfoPanel';
import { useGeneralAccountSection } from '../hooks/useGeneralAccountInfo';
import type { Account, GeneralAccountInfoResponse } from '../model';

type Props = {
  account: Account;
};

type SectionQuery = { isLoading: boolean; error: unknown };

const toRequest = (query: SectionQuery, errorFrom: (error: unknown) => Error | undefined) => ({
  isFetching: query.isLoading,
  error: errorFrom(query.error),
});

// React Query errors here are a RequestError ({ status, message }) or an Error.
// Turn them into an Error carrying a real message so the panel never renders
// "[object Object]" — String() on the RequestError object would do exactly that.
function errorFromQuery(error: unknown): Error | undefined {
  if (!error) {
    return undefined;
  }
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'string') {
    return new Error(error);
  }
  if (typeof error === 'object') {
    const { message, status } = error as { message?: unknown; status?: unknown };
    // RequestError.message is `string | object`; when it isn't a plain string,
    // keep any nested string message, otherwise serialize it and attach the
    // status so the error banner keeps actionable detail instead of a bare
    // "Unknown error".
    let detail = 'Unknown error';
    if (typeof message === 'string') {
      detail = message;
    } else if (message !== undefined && message !== null) {
      const nested = (message as { message?: unknown }).message;
      detail = typeof nested === 'string' ? nested : JSON.stringify(message);
    }
    return new Error(typeof status === 'number' ? `${detail} (status ${status})` : detail);
  }
  return new Error('Unknown error');
}

export function GeneralAcctInfoPanelContainer({ account }: Props) {
  const { enableVitalResourceCountLimits } = useSettings();
  const showLimitIncreases = Boolean(enableVitalResourceCountLimits);

  // Each General-tab section is its own request, so a slow or failing section
  // (e.g. a warehouse-backed one) renders its own loading/error and can't block
  // or take down the others. The limit-increase sections are gated on the flag.
  const launchdarkly = useGeneralAccountSection(account, 'launchdarkly');
  const zendesk = useGeneralAccountSection(account, 'zendesk');
  const integrations = useGeneralAccountSection(account, 'integrations');
  const supportEntitlement = useGeneralAccountSection(account, 'supportEntitlement');
  const dataExport = useGeneralAccountSection(account, 'dataExport');
  const resourceLimits = useGeneralAccountSection(account, 'resourceLimits', showLimitIncreases);
  const rateLimitIncreases = useGeneralAccountSection(account, 'rateLimitIncreases', showLimitIncreases);

  // Each query returns only its own section's keys, so merging their data never
  // clobbers another section; the panel reads each slice for rendering. A `.data`
  // is undefined until its query resolves, which object spread safely ignores.
  const generalData: GeneralAccountInfoResponse = {
    ...launchdarkly.data,
    ...zendesk.data,
    ...integrations.data,
    ...supportEntitlement.data,
    ...dataExport.data,
    ...(showLimitIncreases ? resourceLimits.data : undefined),
    ...(showLimitIncreases ? rateLimitIncreases.data : undefined),
  };

  return (
    <GeneralAcctInfoPanel
      generalData={generalData}
      launchdarklyRequest={toRequest(launchdarkly, errorFromQuery)}
      zendeskRequest={toRequest(zendesk, errorFromQuery)}
      integrationsRequest={toRequest(integrations, errorFromQuery)}
      supportEntitlementRequest={toRequest(supportEntitlement, errorFromQuery)}
      dataExportRequest={toRequest(dataExport, errorFromQuery)}
      resourceLimits={showLimitIncreases ? resourceLimits.data?.resourceLimits : []}
      resourceLimitsRequest={showLimitIncreases ? toRequest(resourceLimits, errorFromQuery) : undefined}
      rateLimitIncreases={showLimitIncreases ? rateLimitIncreases.data?.rateLimitIncreases : []}
      rateLimitIncreasesRequest={showLimitIncreases ? toRequest(rateLimitIncreases, errorFromQuery) : undefined}
    />
  );
}
